import { GoogleGenAI, Type, Schema } from "@google/genai";
import { CaseData } from "../types";

// Helper for ID generation since we can't easily add packages
const generateId = () => Math.random().toString(36).substr(2, 9);

const apiKey = import.meta.env.VITE_API_KEY;
const ai = new GoogleGenAI({ apiKey: apiKey });

// --- CONFIGURAÇÃO DE MODELOS ---
// Arquiteto: Responsável pela lógica complexa, consistência e criação do mundo.
const ARCHITECT_MODEL = "gemini-2.5-pro";

// Narrador: Responsável pelos diálogos dinâmicos e interpretação de personagens.
const NARRATOR_MODEL = "gemini-2.5-flash";

export const getTutorialCase = async (): Promise<CaseData> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const now = Date.now();
      const startGameTime = new Date("2024-01-01T08:00:00").getTime();
      const deadline = startGameTime + (7 * 24 * 60 * 60 * 1000); // 7 days deadline

      const tutorialCase: CaseData = {
        id: 'tutorial-case-001',
        title: "O Mistério do Relógio Parado",
        description: "RELATÓRIO POLICIAL #4092\n\nSTATUS: HOMICÍDIO.\n\nVÍTIMA: Alberto Santos, relojoeiro mestre.\nLOCAL DO CRIME: Oficina do Relojoeiro (LOC_1).\n\nO local está isolado pela perícia. Você tem acesso livre.",
        createdAt: now,
        deadline: deadline,
        status: 'active',
        heroImageUrl: '/tutorialhero.jpg',
        thumbImageUrl: '/tutorialcarrossel.jpg',

        // State Init
        lastSavedTime: startGameTime,
        currentLocationId: 'HQ',
        visitedLocationIds: ['HQ'],
        discoveredLocationIds: ['HQ', 'LOC_1'],
        scoutedLocationIds: ['HQ'],
        metCharacterIds: [],
        knownCharacterNames: { 'CHAR_3': true }, // Expert at HQ is known
        inventory: [],
        logs: [{ timestamp: "08:00", type: 'system', content: 'Caso atribuído.' }],
        userNotes: '',
        suspectAnnotations: {},

        locations: [
          {
            id: "LOC_1",
            name: "Oficina do Relojoeiro",
            description: "Cena do crime. Fitas amarelas e policiais na porta.",
            clues: [
              {
                description: "O cheiro de óleo e sangue é forte. O corpo de Alberto está debruçado sobre a bancada.",
              },
              {
                description: "Há pegadas de barro tamanho 42 perto da porta dos fundos.",
              },
              {
                description: "Você encontra um Relógio de Bolso antigo, parado exatamente às 02:14. A parte de trás tem uma gravação arranhada.",
                isInspectable: true,
                inspectResult: {
                  story: "Ao forçar a tampa do relógio, você encontra um pequeno papel dobrado com os dizeres: 'Encontrar Navalha no Gato Preto amanhã'.",
                  unlocksCharacters: ["CHAR_2"], // Unlocks Rico Navalha
                  unlocksLocations: ["LOC_2"] // Ensures bar is unlocked
                }
              }
            ],
            openTime: "08:00",
            closeTime: "18:00",
            x: 55, // Close to HQ (50,50)
            y: 52,
            type: "public",
            isCrimeScene: true
          },
          {
            id: "LOC_2",
            name: "Bar 'O Gato Preto'",
            description: "Bar suspeito no centro, frequentado por tipos duvidosos.",
            clues: [
              {
                description: "O chão está coberto de serragem e bitucas de cigarro. O barman parece nervoso.",
              },
              {
                description: "Em uma mesa no canto, você nota um copo ainda úmido e um maço de cigarros importados esquecido. Há marcas de lama perto da entrada do banheiro que parecem combinar com as da cena do crime.",
                unlocksCharacters: ["CHAR_2"]
              }
            ],
            openTime: "18:00",
            closeTime: "04:00",
            x: 40,
            y: 60,
            type: "public",
            isCrimeScene: false
          },
          {
            id: "LOC_3",
            name: "Estação de Trem Velha",
            description: "Local abandonado usado para encontros escusos.",
            clues: [
              {
                description: "O local está vazio, não há sinais de movimento recente.",
              }
            ],
            openTime: "00:00",
            closeTime: "23:59",
            x: 80, // Far away
            y: 20,
            type: "public",
            isCrimeScene: false
          },
          {
            id: "LOC_4",
            name: "Mansão dos Silvas",
            description: "Residência de uma família tradicional falida.",
            clues: [
              {
                description: "A casa está empoeirada. O mordomo informa que ninguém sai há dias, mas você nota pneus de carro recém-usados na garagem.",
              }
            ],
            openTime: "09:00",
            closeTime: "17:00",
            x: 20,
            y: 30,
            type: "private",
            isCrimeScene: false
          }
        ],
        characters: [
          {
            id: "CHAR_1",
            name: "Helena",
            role: "witness",
            locationId: "LOC_1",
            description: "A aprendiz jovem e assustada. Estava na loja de manhã.",
            phoneStatus: 'available'
          },
          {
            id: "CHAR_2",
            name: "Rico 'Navalha'",
            role: "suspect",
            locationId: "LOC_2",
            description: "Agiota conhecido. Usa ternos azul marinho e sempre calça botas pesadas.",
            phoneStatus: 'busy'
          },
          {
            id: "CHAR_3",
            name: "Dr. Arnaldo",
            role: "expert",
            locationId: "HQ",
            description: "Médico legista. Informa que a morte ocorreu por trauma contuso por volta das 02:00 da manhã.",
            phoneStatus: 'available'
          }
        ],
        solution: {
          culpritId: "CHAR_2",
          motive: "Alberto devia uma grande quantia em jogos ilegais operados por Rico. A recusa em pagar resultou em uma briga fatal."
        }
      };
      resolve(tutorialCase);
    }, 1000);
  });
};

const CASE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    title: { type: Type.STRING },
    description: { type: Type.STRING },
    locations: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          name: { type: Type.STRING },
          description: { type: Type.STRING },
          clues: {
            type: Type.ARRAY,
            description: "Detailed list of clues found here. MUST link the investigation explicitly by unlocking new POIs.",
            items: {
              type: Type.OBJECT,
              properties: {
                description: { type: Type.STRING, description: "Narrative description of what the detective found." },
                unlocksLocations: { type: Type.ARRAY, items: { type: Type.STRING }, description: "IDs of locations this clue reveals (e.g. ['LOC_3'])." },
                unlocksCharacters: { type: Type.ARRAY, items: { type: Type.STRING }, description: "IDs of characters this clue reveals (e.g. ['CHAR_2'])." },
                isInspectable: { type: Type.BOOLEAN, description: "True if this clue is a physical item (e.g., phone, letter, key) that the player can inspect further." },
                inspectResult: {
                  type: Type.OBJECT,
                  description: "Data for when the player inspects this item. REQUIRED if isInspectable is true.",
                  properties: {
                    story: { type: Type.STRING, description: "The narrative outcome of inspecting this item." },
                    unlocksLocations: { type: Type.ARRAY, items: { type: Type.STRING } },
                    unlocksCharacters: { type: Type.ARRAY, items: { type: Type.STRING } }
                  }
                }
              }
            }
          },
          openTime: { type: Type.STRING },
          closeTime: { type: Type.STRING },
          x: { type: Type.NUMBER, description: "X Coordinate (0-100)" },
          y: { type: Type.NUMBER, description: "Y Coordinate (0-100)" },
          type: { type: Type.STRING, enum: ["public", "private", "restricted"] },
          isCrimeScene: { type: Type.BOOLEAN, description: "True ONLY for the main crime scene." }
        },
        required: ["id", "name", "description", "clues", "openTime", "closeTime", "x", "y", "type", "isCrimeScene"]
      }
    },
    characters: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          name: { type: Type.STRING },
          role: { type: Type.STRING, enum: ["witness", "suspect", "expert"] },
          locationId: { type: Type.STRING },
          description: { type: Type.STRING }
        },
        required: ["id", "name", "role", "locationId", "description"]
      }
    },
    solution: {
      type: Type.OBJECT,
      properties: {
        culpritId: { type: Type.STRING },
        motive: { type: Type.STRING }
      },
      required: ["culpritId", "motive"]
    }
  },
  required: ["title", "description", "locations", "characters", "solution"]
};

export const generateCase = async (
  difficulty: 'Fácil' | 'Médio' | 'Difícil' = 'Médio',
  estimatedTime: 'Curto' | 'Médio' | 'Longo' = 'Médio',
  isSpecialSeason: boolean = false,
  customThemes: string[] = []
): Promise<CaseData> => {
  const defaultThemes = [
    'Traição na alta sociedade decadente', 'Corrupção no departamento de polícia', 'Contrabando na zona portuária',
    'Assassinato em um teatro clássico', 'Segredos obscuros de uma família rica', 'Roubo de arte que terminou em morte',
    'A morte de um detetive particular rival', 'Desaparecimento em um trem noturno', 'Vingança de um soldado renegado',
    'Mistério macabro em um clube de jazz underground', 'Chantagem no mundo do boxe clandestino', 'Sabotagem industrial letal'
  ];
  const themesToUse = customThemes && customThemes.length > 0 ? customThemes : defaultThemes;
  const randomTheme = themesToUse[Math.floor(Math.random() * themesToUse.length)];

  const prompt = `
    Crie um caso de detetive Noir ÚNICO, INÉDITO e CRIATIVO. NUNCA repita nomes de casos anteriores.
    FUJA PADRÕES DO MODELO: Jamais use títulos como "Dália Negra", "Sussurro", ou vilões chamados "Silas", "Blackwood", nem cidades como "Port Nocturne". Invente nomes de cidades, bairros e personagens completamente novos e originais.
    
    Inspiração de Tema: "${randomTheme}"

    - Dificuldade do Mistério: ${difficulty} (ajuste o quão óbvias são as pistas)
    - Extensão da História: ${estimatedTime} (ajuste a profundidade das descrições e do mistério)
    ${isSpecialSeason ? '- Este é um Caso Épico de Temporada. Recheie os cenários e diálogos com muito mistério, lore profundo e uma conspiração maior.' : ''}

    1. Defina um culpado internamente e um motivo sólido (fuja do óbvio). O nome da vítima e o título do caso devem prender a atenção.
    2. Gere 4 locais distintos com nomes e descrições atmosféricas. Um DEVE ter 'isCrimeScene': true.
    3. Para cada local, atribua coordenadas X e Y (0 a 100) espalhadas pela cidade para o mapa.
    4. Gere 3 a 4 personagens (Testemunhas, Suspeitos, Peritos) com profundeza psicológica e nomes únicos.
    5. CRUCIAL MECÂNICA ENCADEADA (Grafos de Pistas): Os locais DEVEM formar um caminho conectado. Ao investigar a cena do crime, o jogador DEVE achar pistas que referenciem (usando 'unlocksLocations' ou 'unlocksCharacters') os próximos locais e suspeitos.
    6. MECÂNICA DE INVESTIGAÇÃO DE ITENS: Você DEVE incluir PELO MENOS UM item físico importante como pista em algum lugar (ex: um celular bloqueado, um diário, uma chave). Para essa pista, defina 'isInspectable': true, e preencha 'inspectResult'. O ato de inspecionar esse item deve revelar uma reviravolta ou o esconderijo do culpado. Exemplo: Achar um celular não diz nada, mas o 'inspectResult.story' dirá "Você quebrou a senha. A última mensagem foi para o Clube Azul", e 'unlocksLocations': ['LOC_3'].

    Lembre-se: A PRIORIDADE É ORIGINALIDADE, ENCADEAMENTO DE PISTAS (Não libere todos os locais e suspeitos de uma vez só! Crie uma rota crítica clara) E ATMOSFERA NOIR.
    Responda EXCLUSIVAMENTE em JSON.
  `;

  try {
    const response = await ai.models.generateContent({
      model: ARCHITECT_MODEL,
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: CASE_SCHEMA,
        temperature: 1 // Temperature 1 força respostas sempre com extrema variabilidade
      }
    });

    if (response.text) {
      const rawData = JSON.parse(response.text);

      const now = Date.now();
      const startGameTime = new Date("2024-01-01T08:00:00").getTime();

      return {
        ...rawData,
        id: generateId(),
        createdAt: now,
        deadline: startGameTime + (7 * 24 * 60 * 60 * 1000),
        status: 'active',
        difficulty,
        estimatedTime,
        isSpecialSeason,

        lastSavedTime: startGameTime,
        currentLocationId: 'HQ',
        discoveredLocationIds: ['HQ', rawData.locations.find((l: any) => l.isCrimeScene)?.id || rawData.locations[0].id],
        scoutedLocationIds: ['HQ'],
        visitedLocationIds: ['HQ'],
        metCharacterIds: [],
        knownCharacterNames: {},
        inventory: [],
        logs: [{ timestamp: "08:00", type: 'system', content: 'Caso iniciado.' }],
        userNotes: '',
        suspectAnnotations: {},
        characters: rawData.characters.map((c: any) => ({
          ...c,
          phoneStatus: Math.random() > 0.3 ? 'available' : 'busy'
        }))
      };
    }
    throw new Error("Resposta vazia do Gemini");
  } catch (error) {
    console.error("Erro ao gerar caso:", error);
    throw error;
  }
};

export const interrogateCharacter = async (name: string, role: string, desc: string, q: string, ctx: string): Promise<string> => {
  // Parse context context to enforce strict location usage
  let locationsList = "";
  try {
    const parsedCtx = JSON.parse(ctx);
    if (parsedCtx.locations) {
      locationsList = parsedCtx.locations.map((l: any) => `- ${l.name}`).join("\n");
    }
  } catch (e) {
    // Fallback if context isn't valid JSON
  }

  const prompt = `
      RPG Detetive Noir (Interrogatório).
      
      PERSONAGEM: ${name}
      FUNÇÃO: ${role}
      DESCRIÇÃO: ${desc}
      
      DADOS DO CASO (Contexto): ${ctx}
      
      PERGUNTA DO DETETIVE: "${q}"
      
      INSTRUÇÕES ESTRITAS DE CONSISTÊNCIA:
      1. Responda APENAS como o personagem. Mantenha o tom Noir/Sombrio.
      2. Mantenha consistência absoluta com as evidências fornecidas nos dados do caso.
      3. **PROIBIDO INVENTAR LOCAIS**: Você NÃO pode criar, sugerir ou mencionar locais físicos na cidade que não estejam na lista de LOCAIS VÁLIDOS abaixo. 
      4. Se o jogador perguntar ou se a resposta lógica envolveria um local que não está na lista, você deve ser vago (ex: "naquele lugar", "pelos becos", "no centro") ou dizer que não sabe, mas NUNCA dê um nome próprio a um estabelecimento novo (como "Clube Azul", "Bar do Zé") que não exista na lista.
      
      LOCAIS VÁLIDOS PARA REFERÊNCIA:
      ${locationsList}
      
      Responda curto e direto.
    `;

  const response = await ai.models.generateContent({
    model: NARRATOR_MODEL,
    contents: prompt
  });
  return response.text || "...";
};

export const convertCaseStructure = async (legacyCase: CaseData): Promise<CaseData> => {
  const partialSchema: Schema = {
    type: Type.ARRAY,
    description: "Array of locations with their new structured clues",
    items: {
      type: Type.OBJECT,
      properties: {
        id: { type: Type.STRING },
        clues: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              description: { type: Type.STRING },
              unlocksLocations: { type: Type.ARRAY, items: { type: Type.STRING } },
              unlocksCharacters: { type: Type.ARRAY, items: { type: Type.STRING } },
              isInspectable: { type: Type.BOOLEAN },
              inspectResult: {
                type: Type.OBJECT,
                properties: {
                  story: { type: Type.STRING },
                  unlocksLocations: { type: Type.ARRAY, items: { type: Type.STRING } },
                  unlocksCharacters: { type: Type.ARRAY, items: { type: Type.STRING } }
                }
              }
            }
          }
        }
      },
      required: ["id", "clues"]
    }
  };

  const strLocations = JSON.stringify(legacyCase.locations.map(l => ({ id: l.id, name: l.name, evidence: l.evidence })));
  const strChars = JSON.stringify(legacyCase.characters.map(c => ({ id: c.id, name: c.name, role: c.role })));

  const prompt = `
    VOCÊ ESTÁ ATUALIZANDO UM CASO LEGADO. Sua tarefa é transformar a string estática de pistas de cada local numa árvore de Pistas mecânica e encadeada.
    
    LOCAÇÕES ATUAIS (contém nome, ID, e a 'evidence' texto antigo):
    \${strLocations}
    
    PERSONAGENS DISPONÍVEIS:
    \${strChars}

    Para CADA locação recebida acima, você deve retornar a chave id e o array de 'clues' analisando a evidence de antes. 
    A descrição da pista deve manter o teor literário original. PREENCHA as sub-arrays 'unlocksLocations' e 'unlocksCharacters' caso a pista aponte para alguém ou para outro lugar citado na lista acima.
    NOVIDADE: Identifique se a pista cita algum item físico manipulável (celular, carta, documento fechado). Se sim, defina 'isInspectable': true, e separe a revelação do item no 'inspectResult.story'. Se a pista não for algo manipulável, omita isInspectable e inspectResult.

    Responda EXCLUSIVAMENTE em JSON o array de locais convertidos.
  `;

  try {
    const response = await ai.models.generateContent({
      model: ARCHITECT_MODEL,
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: partialSchema,
        temperature: 0.4
      }
    });

    if (response.text) {
      const convertedLocationsData = JSON.parse(response.text) as any[];

      const newCase = JSON.parse(JSON.stringify(legacyCase)) as CaseData;

      newCase.locations = newCase.locations.map(loc => {
        const conversionMatch = convertedLocationsData.find(c => c.id === loc.id);
        if (conversionMatch) {
          loc.clues = conversionMatch.clues || [];
          // Deletar old evidence to clean up DB space optional
          delete loc.evidence;
        } else {
          loc.clues = [{
            description: loc.evidence || "Nada encontrado de imediato.",
            unlocksLocations: [],
            unlocksCharacters: []
          }];
        }
        return loc;
      });

      return newCase;
    }
    throw new Error("No response string from Conversor");
  } catch (error) {
    console.error("Erro na Conversão do Legado:", error);
    throw error;
  }
};

export const improveCase = async (currentCase: CaseData, instructions: string): Promise<CaseData> => {
  const prompt = `
    VOCÊ É O ARQUITETO. Recebi um caso de investigação Noir em formato JSON e preciso que você o MELHORE seguindo estas instruções do usuário:
    
    INSTRUÇÕES: "${instructions}"
    
    DADOS ATUAIS DO CASO:
    ${JSON.stringify(currentCase)}

    REGRAS DE MELHORIA:
    1. Mantenha a consistência de nomes e IDs existentes, a menos que as instruções peçam explicitamente para mudar.
    2. Enriqueça as descrições dos locais e pistas para que fiquem mais imersivas e Noir.
    3. Verifique se as conexões (unlocksLocations/unlocksCharacters) fazem sentido e não deixam o jogador travado. Se notar um erro lógico, corrija-o.
    4. Adicione mais 'profundidade' literária aos diálogos implícitos e descrições.
    5. Mantenha o Schema JSON idêntico ao original.
    
    Responda EXCLUSIVAMENTE com o JSON completo do caso atualizado.
  `;

  try {
    const response = await ai.models.generateContent({
      model: ARCHITECT_MODEL,
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: CASE_SCHEMA,
        temperature: 0.8
      }
    });

    if (response.text) {
      const updatedData = JSON.parse(response.text);
      // Re-apply core metadata that AI might have missed or messed up
      return {
        ...currentCase,
        ...updatedData,
        id: currentCase.id, // Ensure ID never changes
        createdAt: currentCase.createdAt || Date.now()
      };
    }
    throw new Error("Resposta vazia do Arquiteto");
  } catch (error) {
    console.error("Erro ao melhorar caso:", error);
    throw error;
  }
};

// Deprecated
export const analyzeEvidence = async (locationName: string, caseContext: string): Promise<string> => {
  return "Analyzing...";
};
export const observeCharacter = async (name: string, role: string, desc: string, ctx: string): Promise<string> => {
  return "Observing...";
};